/**
 * 10.5K — ATRIBUTOS COMPLETOS (aberto pelo ❗ da Bolsa). Layout de lista: cabeçalho com
 * DPS / VIDA / REGEN em destaque + grupos (Geral · Dano · Defesa · Mobilidade · Corrente)
 * listando TUDO que existe no jogo, com o valor final (fórmula única do Game/Player).
 * Só HTML — sem lógica de jogo (recebe CharacterData pronto).
 */
import { i18n } from "../i18n/i18n";
import { fmtNum } from "../utils/format";
import type { CharacterData } from "./CharacterScreen";

type S = CharacterData["stats"];
interface Row {
  key: keyof S;
  labelKey: string;
  fmt: (v: number) => string;
  hint?: (s: S) => string; // texto secundário (ex.: redução real da armadura)
}
interface Group {
  titleKey: string;
  icon: string;
  rows: Row[];
}

const pct = (v: number): string => (Math.round(v * 100) === 0 ? "0%" : `${v > 0 ? "+" : ""}${Math.round(v * 100)}%`);
const pctNeg = (v: number): string => (Math.round(v * 100) === 0 ? "0%" : `-${Math.round(v * 100)}%`);
const num = (v: number): string => fmtNum(v); // 10.7P — K/M/B em números grandes
const fx = (v: number, d = 1, suf = ""): string => `${v.toFixed(d)}${suf}`;
/** 10.9EV — "🛒 +36 comprado (3×)" (vazio se nunca comprou) */
const bought = (amt: string, n: number): string => (n > 0 ? `🛒 +${amt} ${i18n.t("attr.bought")} (${n}×)` : "");

const GROUPS: Group[] = [
  {
    titleKey: "attr.group.general", icon: "✦",
    rows: [
      { key: "moveSpeed", labelKey: "stat.moveSpeed", fmt: (v) => fx(v, 1, " m/s") },
      { key: "maxHp", labelKey: "stat.maxHp", fmt: num, hint: (s) => bought(num(s.boughtHp), s.boughtHpN) }, // 10.9EV
      { key: "regen", labelKey: "stat.regen", fmt: (v) => `${fx(v, 1)} HP/s`, hint: (s) => bought(`${fx(s.boughtRegen, 2)} HP/s`, s.boughtRegenN) }, // 10.9EV
      { key: "lifeSteal", labelKey: "stat.lifeSteal", fmt: (v) => `${fx(v * 100, 1)}%` }, // 10.9EQ
      { key: "armor", labelKey: "stat.armor", fmt: num, hint: (s) => `${pctNeg(s.armor / (s.armor + 100))} ${i18n.t("attr.dmgTaken")}` },
    ],
  },
  {
    titleKey: "attr.group.damage", icon: "⚔️",
    rows: [
      { key: "damage", labelKey: "stat.damage", fmt: num, hint: (s) => bought(num(s.boughtDmg), s.boughtDmgN) }, // 10.9EV
      { key: "dps", labelKey: "stat.dps", fmt: num },
      { key: "attackSpeed", labelKey: "stat.attackSpeed", fmt: (v) => fx(v, 2, "/s") },
      { key: "attackRange", labelKey: "stat.attackRange", fmt: (v) => fx(v, 1, " m") },
      { key: "critChance", labelKey: "stat.criticalChance", fmt: (v) => `${Math.round(v * 100)}%` },
      { key: "critDamage", labelKey: "stat.criticalDamage", fmt: (v) => `${Math.round(v * 100)}%` },
      { key: "dmgSlash", labelKey: "stat.dmgSlash", fmt: pct },
      { key: "dmgPierce", labelKey: "stat.dmgPierce", fmt: pct },
      { key: "dmgBlunt", labelKey: "stat.dmgBlunt", fmt: pct },
      { key: "vsElite", labelKey: "stat.vsElite", fmt: pct },
      { key: "execute", labelKey: "stat.execute", fmt: pct },
    ],
  },
  {
    titleKey: "attr.group.defense", icon: "🛡️",
    rows: [
      { key: "resilience", labelKey: "stat.resilience", fmt: pctNeg },
      { key: "defSlash", labelKey: "stat.defSlash", fmt: pctNeg },
      { key: "defPierce", labelKey: "stat.defPierce", fmt: pctNeg },
      { key: "defBlunt", labelKey: "stat.defBlunt", fmt: pctNeg },
      { key: "block", labelKey: "stat.block", fmt: (v) => `${Math.round(v * 100)}%` },
    ],
  },
  {
    titleKey: "attr.group.mobility", icon: "👟",
    rows: [
      { key: "hookRange", labelKey: "stat.hookRange", fmt: (v) => fx(v, 1, " m") },
      { key: "hookCooldown", labelKey: "stat.hookCooldown", fmt: (v) => fx(v, 1, " s") },
      { key: "sprintDuration", labelKey: "stat.sprintDuration", fmt: (v) => fx(v, 1, " s") },
      { key: "sprintCooldown", labelKey: "stat.sprintCooldown", fmt: (v) => fx(v, 1, " s") },
    ],
  },
  {
    titleKey: "attr.group.chain", icon: "⛓️",
    rows: [
      { key: "chainCount", labelKey: "stat.chainCount", fmt: num },
      { key: "chainDps", labelKey: "stat.chainDps", fmt: num },
      { key: "chainReach", labelKey: "stat.chainReach", fmt: (v) => fx(v, 1, " m") },
    ],
  },
];

/** HTML da tela de atributos (cabeçalho + grupos). Quem chama liga o botão data-a="attr-back". */
export function renderAttributes(d: CharacterData): string {
  const s = d.stats;
  const head = `
    <div class="at-tip-hint">👆 ${i18n.t("attrhelp.hint")}</div>
    <div class="at-head">
      <div class="at-big" data-help="dpsTotal"><span class="at-big-v">${num(s.dps + s.chainDps)}</span><span class="at-big-l">${i18n.t("attr.dps")}</span></div>
      <div class="at-big hp" data-help="maxHp"><span class="at-big-v">${num(s.maxHp)}</span><span class="at-big-l">${i18n.t("attr.hp")}</span></div>
      <div class="at-big rg" data-help="regen"><span class="at-big-v">${fx(s.regen, 1)}</span><span class="at-big-l">${i18n.t("attr.regen")}</span></div>
    </div>
    <div class="at-help at-help-head" hidden></div>
    <div class="at-sub">${i18n.t("character.combatPower")}: <b>${num(d.combatPower)}</b> · ${i18n.t("character.level")} <b>${d.level}</b> · ${i18n.t("voidTree.points")}: <b>${d.voidAvailable}</b></div>`;
  const groups = GROUPS.map((g) => {
    const rows = g.rows
      .map((r) => {
        const v = s[r.key];
        const ht = r.hint ? r.hint(s) : "";
        const hint = ht ? `<span class="at-hint">${ht}</span>` : "";
        // 10.9ER — TOQUE mostra o que o atributo faz (ajuda de quem esta comecando)
        return `<div class="at-row" data-help="${String(r.key)}"><span class="at-lbl"><span class="at-name">${i18n.t(r.labelKey)}<span class="at-q">?</span></span>${hint}</span><b class="at-val">${r.fmt(v)}</b></div><div class="at-help" hidden></div>`;
      })
      .join("");
    return `<div class="at-group"><div class="at-gt"><span>${g.icon}</span>${i18n.t(g.titleKey)}</div>${rows}</div>`;
  }).join("");
  return `<div class="at-top"><button class="btn inv-back" data-a="attr-back">← ${i18n.t("menu.back")}</button><h3 class="at-title">❗ ${i18n.t("attr.title")}</h3></div>${head}<div class="at-groups">${groups}</div><div class="at-foot">${i18n.t("attr.foot")}</div>`;
}

/**
 * 10.9ER — AJUDA DOS ATRIBUTOS. Tocar numa linha (ou num dos 3 numeros grandes do topo) abre a
 * explicacao logo abaixo dela; tocar de novo (ou em outra) fecha. Abre embaixo em vez de balao
 * flutuante: no celular o balao costuma sair cortado na borda da tela.
 */
const boundHelpViews = new WeakSet<HTMLElement>();

export function bindAttributeHelp(root: HTMLElement): void {
  root.querySelectorAll<HTMLElement>("[data-help]").forEach((el) => {
    el.setAttribute("role", "button");
    el.tabIndex = 0;
    el.setAttribute("aria-expanded", "false");
  });
  // The view's HTML is replaced whenever it opens; delegation needs only one listener.
  if (boundHelpViews.has(root)) return;
  boundHelpViews.add(root);
  const closeAll = (): void => {
    root.querySelectorAll<HTMLElement>(".at-help").forEach((h) => { h.hidden = true; });
    root.querySelectorAll(".help-open").forEach((e) => e.classList.remove("help-open"));
    root.querySelectorAll("[data-help]").forEach((e) => e.setAttribute("aria-expanded", "false"));
  };
  root.addEventListener("click", (e) => {
    const el = (e.target as HTMLElement).closest<HTMLElement>("[data-help]");
    if (!el || !root.contains(el)) return;
    const key = el.dataset.help!;
    // numeros grandes do topo usam a caixa do cabecalho; linhas usam a caixa logo abaixo delas
    const box = el.classList.contains("at-big")
      ? root.querySelector<HTMLElement>(".at-help-head")
      : (el.nextElementSibling as HTMLElement | null);
    if (!box || !box.classList.contains("at-help")) return;
    const wasOpen = !box.hidden && box.dataset.key === key;
    closeAll();
    if (wasOpen) return;
    box.dataset.key = key;
    box.textContent = i18n.t(`attrhelp.${key}`);
    box.hidden = false;
    el.classList.add("help-open");
    el.setAttribute("aria-expanded", "true");
  });
  root.addEventListener("keydown", (e) => {
    const el = (e.target as HTMLElement).closest<HTMLElement>("[data-help]");
    if (el && (e.key === "Enter" || e.key === " ")) { e.preventDefault(); el.click(); }
  });
}
